import { useStytchB2BClient, useStytchMemberSession } from "@stytch/react/b2b";
import { OrganizationBySlugMatch } from "@stytch/vanilla-js";
import { useState, ChangeEvent, useEffect } from "react";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import LoginModal from "../../components/LoginModal";
import PalommaLogo from "../../components/PalommaLogo";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const LoginOrSignup = (): JSX.Element => {
  const stytch = useStytchB2BClient();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [organization, setOrganization] = useState<OrganizationBySlugMatch | null>(null);
  const merchantId = "palomma";
  const { session } = useStytchMemberSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (session) {
      navigate(`/home`);
      return;
    }
  }, [session, navigate]);

  const sendEmailMagicLink = async (): Promise<void> => {
    try {
      if (organization) {
        await stytch.magicLinks.email.loginOrSignup({
          email_address: email,
          organization_id: organization.organization_id,
          locale: "es",
        });
      }
    } catch (error) {
      console.error("Error sending magic link:", error);
    }
  };

  useEffect(() => {
    const fetchOrganization = async () => {
      if (merchantId) {
        try {
          const response = await stytch.organization.getBySlug({
            organization_slug: merchantId,
          });
          if (response.organization) {
            setOrganization(response.organization);
          } else {
            console.log("No organization found");
          }
        } catch (error) {
          console.error("Error fetching organization:", error);
        }
      }
    };

    fetchOrganization();
  }, [stytch, merchantId]);

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsEmailValid(validateEmail(newEmail));
  };

  const handleClick = async (): Promise<void> => {
    setIsSubmitting(true);
    setIsOpen(true);
    await sendEmailMagicLink();
    setIsSubmitting(false);
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 px-4 py-8 md:px-6 lg:px-9">
      <div className="flex w-full max-w-md flex-col gap-8">
        {/* Logo */}
        <div className="flex justify-center">
          <PalommaLogo height={80} width={80} />
        </div>

        {/* Main Card */}
        <div className="flex flex-col justify-center rounded-2xl bg-white p-6 shadow-xl md:p-10">
          <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="mb-2 text-center">
              <h1 className="mb-2 text-2xl font-bold text-palomma-secondary">
                Dashboard Interno
              </h1>
              <p className="text-sm text-gray-600">
                Accede a la plataforma de administración
              </p>
            </div>

            {/* Email Field */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-semibold text-gray-700">
                Correo electrónico <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="admin@palomma.com"
                  className="bg-gray-50 pl-10 hover:border-gray-300 focus:border-palomma-primary focus:bg-white"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleClick}
              className="w-full bg-palomma-primary text-white hover:bg-palomma-primary-dark disabled:bg-gray-400"
              size="lg"
              disabled={!isEmailValid || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 size-4" />
              )}
              Enviar enlace de acceso
            </Button>

            {/* Help text */}
            <p className="text-center text-xs text-gray-500">
              Al continuar, recibirás un enlace mágico en tu correo para acceder de forma segura.
            </p>
          </div>
        </div>
      </div>

      <LoginModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setEmail("");
          setIsEmailValid(false);
        }}
        onClick={sendEmailMagicLink}
      />
    </div>
  );
};

export default LoginOrSignup;
